import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { EnvelopeSimpleIcon } from '@phosphor-icons/react/ssr';
import { useAuth } from '../AuthProvider';
import { useToaster } from '../Toaster';
import { getSocialIdToken, type SocialProviderKey } from '../../utils/firebase';
import './SocialLoginButtons.css';

type SocialProviderConfig = {
  key: SocialProviderKey;
  label: string;
  registerLabel: string;
  icon: React.ReactNode;
  modifier: string;
};

const SOCIAL_PROVIDERS_CONFIG: SocialProviderConfig[] = [
  {
    key: 'google',
    label: 'Continue with Google',
    registerLabel: 'Sign up with Google',
    icon: (
      <img
        src="/images/icons/google.png"
        alt="Google"
        className="social-login-buttons__provider-icon"
      />
    ),
    modifier: 'social-login-buttons__btn--google',
  },
];

export interface SocialLoginButtonsProps {
  /** Called after a successful sign-in so parent can redirect */
  onSuccess?: () => void;
  /** Set to true when shown on the register page to adjust label text */
  isRegister?: boolean;
  /** Called with the email address after a magic link is successfully sent */
  onMagicLinkSent?: (email: string) => void;
}

export const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({
  onSuccess,
  isRegister = false,
  onMagicLinkSent,
}) => {
  const { socialLogin, sendPasswordlessLink } = useAuth();
  const toaster = useToaster();

  const [loadingProvider, setLoadingProvider] =
    useState<SocialProviderKey | null>(null);
  const [showEmailLink, setShowEmailLink] = useState(false);
  const [emailLinkAddress, setEmailLinkAddress] = useState('');
  const [emailLinkLoading, setEmailLinkLoading] = useState(false);

  const handleSocialSignIn = async (providerKey: SocialProviderKey) => {
    if (loadingProvider) return;
    setLoadingProvider(providerKey);
    try {
      const idToken = await getSocialIdToken(providerKey);
      const success = await socialLogin(idToken);
      if (success && onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const providerName =
        providerKey.charAt(0).toUpperCase() + providerKey.slice(1);
      const msg =
        (err as { message?: string })?.message ||
        `${providerName} sign-in failed. Please try again.`;
      toaster.error(msg);
    } finally {
      setLoadingProvider(null);
    }
  };

  const handleSendEmailLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (emailLinkLoading || !emailLinkAddress) return;
    setEmailLinkLoading(true);
    try {
      await sendPasswordlessLink(emailLinkAddress);
      if (onMagicLinkSent) {
        onMagicLinkSent(emailLinkAddress);
      }
    } catch {
      // Errors are shown via toaster inside sendPasswordlessLink
    } finally {
      setEmailLinkLoading(false);
    }
  };

  return (
    <Box className="social-login-buttons">
      {SOCIAL_PROVIDERS_CONFIG.map(
        ({ key, label, registerLabel, icon, modifier }) => (
          <Button
            key={key}
            fullWidth
            variant="outlined"
            size="large"
            onClick={() => handleSocialSignIn(key)}
            disabled={!!loadingProvider}
            className={`social-login-buttons__btn ${modifier}`}
            startIcon={
              loadingProvider === key ? <CircularProgress size={18} /> : icon
            }
          >
            {isRegister ? registerLabel : label}
          </Button>
        )
      )}

      {!showEmailLink && (
        <Button
          fullWidth
          variant="outlined"
          size="large"
          onClick={() => setShowEmailLink(true)}
          className="social-login-buttons__btn social-login-buttons__btn--magic"
          startIcon={<EnvelopeSimpleIcon size={18} />}
        >
          {isRegister ? 'Sign up with email link' : 'Sign in with magic link'}
        </Button>
      )}

      {showEmailLink && (
        <Box
          component="form"
          onSubmit={handleSendEmailLink}
          className="social-login-buttons__email-form"
        >
          <TextField
            type="email"
            label="Your email address"
            value={emailLinkAddress}
            onChange={e => setEmailLinkAddress(e.target.value)}
            fullWidth
            size="small"
            required
            autoFocus
          />
          <Box className="social-login-buttons__email-actions">
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={emailLinkLoading || !emailLinkAddress}
              className="social-login-buttons__send-btn"
            >
              {emailLinkLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                'Send magic link'
              )}
            </Button>
            <Button
              variant="text"
              size="large"
              onClick={() => {
                setShowEmailLink(false);
                setEmailLinkAddress('');
              }}
              className="social-login-buttons__cancel-btn"
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
